import React, { useCallback, useState } from 'react'
import { getInsightContentTypeDisplayText } from 'components/insightContent/utils/utils'
import { Theme } from 'features/ui/types'
import styled from 'styled-components'
import { Trans } from 'react-i18next'
import { useDisplayTheme } from 'hooks/useUiState'
// @ts-ignore
import { Tooltip, Dialog } from '@netbasequid/core'
// @ts-ignore
import { sys } from '@netbasequid/theme'
import AccordionSectionType from 'components/insightContent/enum/AccordionSectionType'
import { InsightData } from 'schemas/action/InsightDataSchema'
import CountType, { CountTypeStrings } from 'enums/CountType'
import { InsightContentSettingType } from 'components/insightContent/types'
import AccordionGroup from 'components/insightContent/visualization/AccordionGroup'
import InsightContentType from 'components/insightContent/enum/InsightContentType'
import { MetricTimeUnitStrings } from 'enums/MetricTimeUnit'
import { sendTypedMessage } from 'utils/iframeUtil'
import { toExternal } from 'vo/util'
import {
  TopInfoBox,
  TopInfoRight,
  StyledTermContainer,
  StyledTermContent
} from 'components/insightContent/visualization/InfoBox/index.css'
import GoToAnalysisButton from 'components/insightContent/visualization/GoToAnalysisButton'
import InsightDialog from 'components/insightContent/visualization/InsightDialog'
import InsightViewOption, { InsightViewOptionStrings } from 'enums/InsightViewOption'
import { AUTO_ATTR_KEY, AutomationId } from 'utils/automationUtil'

import { getLabelByViewOption } from './utils'

const DIALOG_CONTENT_HEIGHT = '460px'

const TitleTooltipContent = ({
  selectedInsights,
  viewOption
}: {
  selectedInsights?: InsightData[]
  viewOption?: InsightViewOptionStrings
}) => {
  switch (viewOption) {
    case InsightViewOption.DOC_HIERARCHY: {
      const names = selectedInsights?.[0]?.searchName?.split('+') || []
      return names.length > 1 ? (
        <div>
          {names.map((name, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={index}>{name}</div>
          ))}
        </div>
      ) : (
        <span>{names[0]}</span>
      )
    }
    case InsightViewOption.TAGS:
    default: {
      return <span>{selectedInsights?.[0]?.searchName}</span>
    }
  }
}

const StyledName = styled.span.attrs({
  [AUTO_ATTR_KEY]: AutomationId.insightModal.insightContent.overview.selectedTerm
})`
  font-weight: ${sys.fontWeight.semibold};
  margin-left: 3px;
  margin-right: 3px;
`

const NameMeasureTitle = ({
  countType,
  selectedInsights,
  viewOption
}: {
  countType: CountTypeStrings
  selectedInsights?: InsightData[]
  viewOption?: InsightViewOptionStrings
}) => {
  let titleKey = ''
  switch (countType) {
    case CountType.MENTIONS:
      titleKey = 'mentionsWith'
      break
    case CountType.POSTS:
      titleKey = 'postsWith'
      break
    default:
  }

  return (
    <Trans i18nKey={titleKey} values={{ name: getLabelByViewOption(selectedInsights, viewOption) }}>
      <StyledName />
    </Trans>
  )
}

const InsightModal = ({
  open,
  title,
  countType,
  selectedInsights,
  onClose,
  detailSetting,
  timeUnits,
  forceNotScale,
  widgetSettingWithSelection
}: {
  open: boolean
  title: string | undefined
  countType: CountTypeStrings
  selectedInsights: InsightData[] | undefined
  onClose: () => void
  detailSetting?: InsightContentSettingType
  timeUnits?: MetricTimeUnitStrings[]
  forceNotScale?: boolean
  widgetSettingWithSelection?: any
}) => {
  const theme = useDisplayTheme() as Theme
  const [displayTooltip, setDisplayTooltip] = useState(false)
  const mustHasTooltip =
    detailSetting?.viewOption === InsightViewOption.DOC_HIERARCHY &&
    (detailSetting.selectionFilter.includedProductHierarchy || []).length > 1

  const checkTermContent = useCallback(
    (node: HTMLDivElement) => {
      if (!node) return
      setDisplayTooltip(mustHasTooltip || node.scrollHeight! > node.clientHeight!)
    },
    [mustHasTooltip]
  )

  function goToAnalysis() {
    sendTypedMessage('goToAnalysis', toExternal(widgetSettingWithSelection))
  }

  return (
    <InsightDialog open={open} onClose={onClose}>
      <Dialog.DialogTitle>{title}</Dialog.DialogTitle>
      <TopInfoBox mode={theme}>
        <StyledTermContainer ref={checkTermContent}>
          <Tooltip
            mode={theme}
            enabled={displayTooltip}
            content={
              <TitleTooltipContent
                selectedInsights={selectedInsights}
                viewOption={detailSetting?.viewOption}
              />
            }>
            <StyledTermContent mode={theme}>
              {getLabelByViewOption(selectedInsights, detailSetting?.viewOption)}
            </StyledTermContent>
          </Tooltip>
        </StyledTermContainer>

        <TopInfoRight>
          <GoToAnalysisButton onClick={() => goToAnalysis()} />
        </TopInfoRight>
      </TopInfoBox>
      <Dialog.DialogContent height={DIALOG_CONTENT_HEIGHT}>
        <AccordionGroup
          timeUnits={timeUnits}
          forceNotScale={forceNotScale}
          countType={countType}
          insightContentsConfig={[
            {
              accordionTitle: getInsightContentTypeDisplayText(AccordionSectionType.SUMMARY),
              accordionContents: [
                {
                  insightContentType: InsightContentType.TOTAL_MEASURE,
                  detailSetting
                },
                {
                  insightContentType: InsightContentType.NAME_MEASURE,
                  detailSetting,
                  renderNameMeasureTitleContent: () => (
                    <NameMeasureTitle
                      countType={countType}
                      selectedInsights={selectedInsights}
                      viewOption={detailSetting?.viewOption}
                    />
                  )
                },
                ...(widgetSettingWithSelection?.showStarRating
                  ? [
                      {
                        insightContentType: InsightContentType.OVERALL_STAR_RATINGS_AVERAGE,
                        detailSetting
                      }
                    ]
                  : []),
                ...(widgetSettingWithSelection?.showSurveyScore
                  ? [
                      {
                        insightContentType: InsightContentType.OVERALL_SURVEY_SCORES_AVERAGE,
                        detailSetting
                      }
                    ]
                  : [])
              ]
            },
            {
              accordionTitle: getInsightContentTypeDisplayText(AccordionSectionType.SOUND_BITE),
              accordionContents: [
                {
                  insightContentType: InsightContentType.HEADER_TWEET_SOUND_BITE,
                  detailSetting
                }
              ]
            }
          ]}
        />
      </Dialog.DialogContent>
      <Dialog.DialogActions />
    </InsightDialog>
  )
}

export default InsightModal
